import { ChevronRight, Clock, Sigma } from 'lucide-react';
import { Button, Card, CardContent, cn, MathText } from '@jeraisonne/ui';
import { SUBJECTS } from '@jeraisonne/db';
import type { DailyExerciseResult } from '@/lib/student-objectives/objective-types';

const DIFFICULTY_META = {
  easy: { label: 'Facile', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  medium: { label: 'Moyen', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  hard: { label: 'Difficile', cls: 'text-red-700 bg-red-50 border-red-200' },
} as const;

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));

interface Props {
  exercise: DailyExerciseResult | null;
  isLoading: boolean;
  onStart: (exerciseId: string) => void;
  inProgress?: boolean;
  /** Ouverture en cours après le clic : spinner + bouton désactivé. */
  isStarting?: boolean;
}

export function DailyExerciseCard({
  exercise,
  isLoading,
  onStart,
  inProgress = false,
  isStarting = false,
}: Props) {
  if (isLoading) {
    return (
      <Card className="border-ink-100 animate-pulse">
        <CardContent className="pt-4">
          <div className="bg-ink-100 mb-3 h-4 w-32 rounded" />
          <div className="bg-ink-100 mb-2 h-5 w-48 rounded" />
          <div className="bg-ink-100 h-4 w-24 rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!exercise) {
    return (
      <Card className="border-ink-100">
        <CardContent className="pt-4">
          <p className="text-ink-400 text-sm">Pas d'exercice du jour disponible pour ton niveau.</p>
        </CardContent>
      </Card>
    );
  }

  const diff = DIFFICULTY_META[exercise.difficulty];
  const illustrationUrl = exercise.illustrationUrl;

  return (
    <Card className="border-ink-100 overflow-hidden">
      <CardContent className="flex flex-col gap-4 p-4 sm:p-5">
        <div className="flex items-start gap-4">
          {illustrationUrl ? (
            <img
              src={illustrationUrl}
              alt=""
              loading="lazy"
              aria-hidden="true"
              className="h-20 w-20 shrink-0 rounded-2xl object-cover shadow-[0_6px_16px_rgba(5,102,242,0.18)] sm:h-24 sm:w-24"
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[linear-gradient(180deg,#3b8bff,#0566f2)] text-white shadow-[0_6px_16px_rgba(5,102,242,0.3)]"
            >
              <Sigma className="h-8 w-8" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className="bg-primary-50 text-primary-600 rounded-full px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide">
                Exercice du jour
              </span>
              {inProgress && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                  En cours
                </span>
              )}
            </div>
            <p className="font-display text-ink-900 text-lg font-bold leading-snug">
              <MathText>{exercise.title}</MathText>
            </p>
            <p className="text-ink-500 mt-0.5 text-sm">
              {SUBJECT_LABEL[exercise.subject] ?? exercise.subject}
              {exercise.chapterTitle ? ` · ${exercise.chapterTitle}` : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn('rounded-full border px-2.5 py-1 text-xs font-bold', diff.cls)}>
              {diff.label}
            </span>
            <span className="text-ink-500 flex items-center gap-1 text-xs">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />~{exercise.estimatedMinutes} min
            </span>
            {exercise.source === 'fallback' && (
              <span className="text-ink-400 text-xs italic">suggestion</span>
            )}
          </div>
          <Button
            variant={inProgress ? 'accent' : 'primary'}
            onClick={() => onStart(exercise.exerciseId)}
            loading={isStarting}
            className="w-full sm:w-auto"
          >
            {isStarting ? 'Ouverture…' : inProgress ? 'Reprendre' : 'Commencer'}
            {!isStarting && <ChevronRight className="h-4 w-4" aria-hidden="true" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
